import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import projectsService from '../services/projectsService';
import { CompletionLetterPayload } from '../types';

const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
};

const LETTER_NUMBER_PATTERN = /^\d{5}$/;

const EMPTY_FORM: CompletionLetterPayload = {
  office_name: '',
  office_address: '',
  todays_date: '',
  project_name: '',
  project_nd_number: '',
  client_name: '',
  PO: '',
  project_reference_nd_offer: '',
  point_of_contact_client_name: '',
  project_start_date: '',
  project_revised_end_date: '',
  letter_number: '',
  nd_signatory_name: '',
  nd_signatory_title: '',
};

function buildLetterId(projectNdNumber: string, letterNumber: string): string {
  const nd = projectNdNumber.trim();
  const suffix = letterNumber.trim();
  if (!nd || !LETTER_NUMBER_PATTERN.test(suffix)) {
    return '';
  }
  return `${nd}-PJM-LET-${suffix}`;
}

interface CompletionLetterDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
}

const CompletionLetterDialog: React.FC<CompletionLetterDialogProps> = ({
  open,
  onClose,
  projectId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [form, setForm] = useState<CompletionLetterPayload>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const loadDefaults = async () => {
      setLoading(true);
      setError(null);
      setSubmitted(false);
      try {
        const defaults = await projectsService.getCompletionLetterDefaults(projectId);
        if (!cancelled) {
          setForm({ ...EMPTY_FORM, ...defaults });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load completion letter defaults');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const letterIdPreview = useMemo(
    () => buildLetterId(form.project_nd_number, form.letter_number),
    [form.project_nd_number, form.letter_number],
  );

  const letterNumberError = submitted && !LETTER_NUMBER_PATTERN.test(form.letter_number.trim())
    ? 'Enter exactly 5 digits (e.g. 00001)'
    : undefined;

  const signatoryNameError = submitted && !form.nd_signatory_name.trim()
    ? 'Signatory name is required'
    : undefined;

  const signatoryTitleError = submitted && !form.nd_signatory_title.trim()
    ? 'Signatory title is required'
    : undefined;

  const isFormValid =
    Boolean(form.project_nd_number.trim()) &&
    LETTER_NUMBER_PATTERN.test(form.letter_number.trim()) &&
    Boolean(form.nd_signatory_name.trim()) &&
    Boolean(form.nd_signatory_title.trim());

  const handleChange = (field: keyof CompletionLetterPayload) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleLetterNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 5);
    setForm((prev) => ({ ...prev, letter_number: digitsOnly }));
  };

  const handleGenerate = async () => {
    setSubmitted(true);
    if (!isFormValid) {
      return;
    }

    setGenerating(true);
    setError(null);
    try {
      await projectsService.generateCompletionLetter(projectId, form);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate completion letter');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (generating) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: surfaceBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${panelBorder}`,
          py: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdfIcon sx={{ color: NAUTILUS.sand }} />
            <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
              Generate Completion Letter
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={generating}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the letter values below. Client-facing blanks in the PDF remain editable after download.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Office name"
                  value={form.office_name}
                  onChange={handleChange('office_name')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Today's date"
                  type="date"
                  value={form.todays_date}
                  onChange={handleChange('todays_date')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Office address"
                  value={form.office_address}
                  onChange={handleChange('office_address')}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Project name"
                  value={form.project_name}
                  onChange={handleChange('project_name')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Project ND number"
                  value={form.project_nd_number}
                  onChange={handleChange('project_nd_number')}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Letter number"
                  value={form.letter_number}
                  onChange={handleLetterNumberChange}
                  fullWidth
                  size="small"
                  required
                  inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                  error={Boolean(letterNumberError)}
                  helperText={
                    letterNumberError ||
                    (letterIdPreview
                      ? `Letter ID: ${letterIdPreview}`
                      : '5-digit suffix (e.g. 00001)')
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Client name"
                  value={form.client_name}
                  onChange={handleChange('client_name')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="PO"
                  value={form.PO}
                  onChange={handleChange('PO')}
                  fullWidth
                  size="small"
                  helperText="Defaults to the project reference contract"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ND Proposal"
                  value={form.project_reference_nd_offer}
                  onChange={handleChange('project_reference_nd_offer')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Point of contact (client)"
                  value={form.point_of_contact_client_name}
                  onChange={handleChange('point_of_contact_client_name')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Project start date"
                  type="date"
                  value={form.project_start_date}
                  onChange={handleChange('project_start_date')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Project revised end date"
                  type="date"
                  value={form.project_revised_end_date}
                  onChange={handleChange('project_revised_end_date')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ND signatory name"
                  value={form.nd_signatory_name}
                  onChange={handleChange('nd_signatory_name')}
                  fullWidth
                  size="small"
                  required
                  error={Boolean(signatoryNameError)}
                  helperText={signatoryNameError || 'Defaults to the project manager'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ND signatory title"
                  value={form.nd_signatory_title}
                  onChange={handleChange('nd_signatory_title')}
                  fullWidth
                  size="small"
                  required
                  error={Boolean(signatoryTitleError)}
                  helperText={signatoryTitleError}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading || generating}
          startIcon={generating ? <CircularProgress size={18} sx={{ color: NAUTILUS.deepOcean }} /> : <PictureAsPdfIcon />}
          sx={{
            bgcolor: NAUTILUS.sand,
            color: NAUTILUS.deepOcean,
            fontWeight: 600,
            '&:hover': {
              bgcolor: alpha(NAUTILUS.sand, 0.85),
            },
            '&:disabled': {
              bgcolor: alpha(NAUTILUS.sand, 0.3),
            },
          }}
        >
          {generating ? 'Generating…' : 'Generate PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletionLetterDialog;
